import type { ManuscriptDocument } from '../../lib/pdf';
import type { PatternRunResult } from '../../lib/patterns';
import { methodologyFindingId, llmMethodologyFindingId } from '../../lib/patterns/finding-id';
import type { LlmItemGrade } from '../../lib/llm';
import { FindingActionButtons, actionStyles, type EditorAction } from './ActionButtons';

interface Props {
  manuscript: ManuscriptDocument | null;
  analysis: PatternRunResult | null;
  itemGrades: LlmItemGrade[] | null;
  findingActions: Record<string, EditorAction | null>;
  onFindingAction: (id: string, next: EditorAction | null) => void;
}

const STATUS_COLOR: Record<string, string> = {
  reported: 'text-emerald-700 bg-emerald-50',
  partial: 'text-amber-800 bg-amber-50',
  missing: 'text-red-700 bg-red-50',
  not_applicable: 'text-slate-500 bg-slate-50',
};

const SEVERITY_COLOR: Record<string, string> = {
  red: 'text-red-700 bg-red-50',
  amber: 'text-amber-800 bg-amber-50',
  info: 'text-slate-600 bg-slate-50',
};

export function MethodologyChecklist({
  manuscript,
  analysis,
  itemGrades,
  findingActions,
  onFindingAction,
}: Props) {
  if (!manuscript || !analysis) {
    return (
      <section className="ms-card">
        <div className="ms-ribbon ms-ribbon--indigo">
          <h2>Methodology checklist</h2>
        </div>
        <p className="text-xs leading-relaxed text-slate-600">
          For the matched guideline, the pattern engine locates the passage that addresses
          each checklist item and shows you that evidence — it does not itself judge
          compliance. <strong>&ldquo;Not located&rdquo;</strong> means the engine could not
          find it (verify against the manuscript; it is not proof of omission). With the
          optional LLM enabled, each item also gets a{' '}
          <em>reported / partial / missing</em> judgment with a short rationale.
        </p>
      </section>
    );
  }

  const { methodology } = analysis;

  if (!methodology) {
    return (
      <section className="ms-card">
        <div className="ms-ribbon ms-ribbon--indigo">
          <h2>Methodology checklist</h2>
        </div>
        <p className="text-xs leading-relaxed text-slate-600">
          No EQUATOR checklist applies for this study type — assess against the appropriate
          reporting guideline and journal norms.
        </p>
      </section>
    );
  }

  const total = methodology.items.length;
  const located = methodology.items.filter((i) => i.status === 'located').length;
  const missingRed = methodology.items.filter(
    (i) => i.status === 'not_located' && i.severityIfMissing === 'red',
  );
  const llmJudged = itemGrades && itemGrades.length > 0;

  return (
    <section className="ms-card">
      <div className="ms-ribbon ms-ribbon--indigo">
        <h2>Methodology checklist · {methodology.guidelineId}</h2>
      </div>
      <p className="mt-1 text-sm text-slate-900">
        <span className="font-medium">{located}</span> of {total} items located by pattern engine
        <span className="ml-1 text-xs text-slate-500">
          ({Math.round(methodology.locatedFraction * 100)}%)
        </span>
      </p>
      <p className="mt-1 text-[11px] text-slate-500">
        {llmJudged
          ? `LLM judged ${itemGrades!.length} item${itemGrades!.length === 1 ? '' : 's'}.`
          : 'Evidence located for each item — the editor judges compliance from the located text.'}
      </p>

      {llmJudged && (
        <div className="mt-2">
          <p className="text-xs font-medium text-slate-700">LLM-graded items:</p>
          <ul className="mt-1 space-y-1">
            {itemGrades!.slice(0, 8).map((g) => {
              const fid = llmMethodologyFindingId(g.itemId, g.status);
              const action = findingActions[fid] ?? null;
              const styles = actionStyles(action);
              return (
                <li
                  key={g.itemId}
                  className={`flex items-start justify-between gap-2 rounded px-2 py-1 text-[11px] ${STATUS_COLOR[g.status] ?? 'bg-slate-50'} ${styles.container}`}
                >
                  <div className={`flex-1 ${styles.text}`}>
                    <span className="font-mono font-medium">{g.itemId}</span> ·{' '}
                    <span className="uppercase tracking-wide">{g.status.replace('_', ' ')}</span>
                    <p className="mt-0.5 text-slate-700">{g.rationale}</p>
                    {g.evidenceSpan && (
                      <p className="mt-0.5 italic text-slate-500">&ldquo;{g.evidenceSpan}&rdquo;</p>
                    )}
                  </div>
                  <FindingActionButtons
                    action={action}
                    onChange={(next) => onFindingAction(fid, next)}
                    compact
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {missingRed.length > 0 && !llmJudged && (
        <div className="mt-2">
          <p className="text-xs font-medium text-red-700">
            High-severity items not located ({missingRed.length}):
          </p>
          <ul className="mt-1 space-y-1">
            {missingRed.slice(0, 5).map((item) => {
              const fid = methodologyFindingId(item.itemId, 'not_located');
              const action = findingActions[fid] ?? null;
              const styles = actionStyles(action);
              return (
                <li
                  key={item.itemId}
                  className={`flex items-start justify-between gap-2 rounded px-2 py-1 text-[11px] ${SEVERITY_COLOR.red} ${styles.container}`}
                >
                  <div className={`flex-1 ${styles.text}`}>
                    <span className="font-mono font-medium">{item.itemId}</span> · {item.itemText}
                  </div>
                  <FindingActionButtons
                    action={action}
                    onChange={(next) => onFindingAction(fid, next)}
                    compact
                  />
                </li>
              );
            })}
            {missingRed.length > 5 && (
              <li className="text-[11px] text-slate-500">… and {missingRed.length - 5} more</li>
            )}
          </ul>
        </div>
      )}
    </section>
  );
}
